import type { Logger } from "pino";
import type { AgentManager, ManagedAgent } from "./agent-manager.js";
import type { AgentStorage } from "./agent-storage.js";
import type { AgentProvider } from "./agent-sdk-types.js";
import { getProviderFullAccessModeId } from "./provider-manifest.js";
import { scheduleAgentMetadataGeneration } from "./agent-metadata-generator.js";
import { escapeAppostleSystemAttr, setupFinishNotification, startAgentRun } from "./mcp-shared.js";
import {
  getClaudeModels,
  normalizeClaudeRuntimeModelId,
} from "./providers/claude/claude-models.js";

/**
 * Resolve a bare family alias ("opus", "sonnet", "haiku", "fable") to the
 * newest catalog model of that family. Catalog order is newest-first per
 * family; skip `[1m]` variants so aliases pick the standard context window.
 */
function resolveClaudeFamilyAlias(
  family: string,
): { model: string; clearThinking: boolean } | null {
  const match = getClaudeModels().find(
    (m) => m.id.startsWith(`claude-${family}-`) && !m.id.endsWith("[1m]"),
  );
  if (!match) return null;
  // Models without thinking options (e.g. Haiku) must not inherit the
  // parent's thinkingOptionId — the SDK rejects it.
  return { model: match.id, clearThinking: !match.thinkingOptions?.length };
}

export type HandoffSpawnedCallback = (params: {
  parentAgentId: string;
  parentAgentCwd: string;
  parentAgentOwnerUserId: string;
  childAgentId: string;
  childAgentTitle: string | null;
  /**
   * Users the parent (and therefore the inherited child) is shared with.
   * The tab fan-out must materialise a "Shared with me" tab for each of
   * them, or the disciple is invisible to every watcher except the owner.
   */
  sharees: Array<{ userId: string }>;
}) => Promise<void> | void;

export function resolveHandoffModel(
  requestedModel: string | undefined,
  targetProvider: AgentProvider,
  parentModel: string | undefined,
  parentThinkingOptionId: string | undefined,
): { model: string | undefined; thinkingOptionId: string | undefined } {
  if (!requestedModel) {
    return { model: parentModel, thinkingOptionId: parentThinkingOptionId };
  }
  if (targetProvider === "claude") {
    const trimmed = requestedModel.trim().toLowerCase();
    if (/^(opus|sonnet|haiku|fable)$/.test(trimmed)) {
      const alias = resolveClaudeFamilyAlias(trimmed);
      if (alias) {
        return {
          model: alias.model,
          thinkingOptionId: alias.clearThinking ? undefined : parentThinkingOptionId,
        };
      }
    }
    // Normalize near-miss forms ("opus-4-8", "opus 4.8", missing prefix)
    // to a catalog ID; reject strings we can't resolve instead of silently
    // handing the SDK an unknown model (which falls back unpredictably).
    const normalized = normalizeClaudeRuntimeModelId(requestedModel);
    const catalog = getClaudeModels();
    const resolved = catalog.find((m) => m.id === normalized) ?? null;
    if (!resolved) {
      const available = catalog.map((m) => m.id).join(", ");
      throw new Error(
        `handoff: unknown Claude model "${requestedModel}". Available: ${available}, or a tier alias (opus, sonnet, haiku, fable).`,
      );
    }
    return {
      model: resolved.id,
      thinkingOptionId: resolved.thinkingOptions?.length ? parentThinkingOptionId : undefined,
    };
  }
  return { model: requestedModel, thinkingOptionId: parentThinkingOptionId };
}

export function buildHandoffBriefEnvelope(params: {
  shepherdId: string;
  shepherdTitle: string;
  taskBrief: string;
}): string {
  const { shepherdId, shepherdTitle, taskBrief } = params;
  const contract = [
    "## How to finish this session",
    "",
    "Your final action in this session MUST be a call to the `report` tool",
    "(`mcp__appostle__report`). The parent agent that handed off to you",
    "cannot see this conversation — it only sees the `message` you pass to",
    "`report`.",
    "",
    "- Lead with a one-line verdict, then fill in detail.",
    "- Make the report self-contained — include file paths, decisions, and",
    "  any blockers. The parent has no access to your timeline.",
    "- Do NOT end the session without calling `report`, even if the answer",
    "  is short, negative, or you got blocked early. A short report is fine;",
    "  silence is not.",
    "- You may call `report` multiple times for interim updates; the final",
    "  call should be the wrap-up.",
    "",
    "## Brief from your shepherd",
    "",
  ].join("\n");
  return [
    `<appostle-system kind="handoff-brief" agent-id="${escapeAppostleSystemAttr(shepherdId)}" title="${escapeAppostleSystemAttr(shepherdTitle)}">`,
    contract,
    "<display>",
    taskBrief,
    "</display>",
    "</appostle-system>",
  ].join("\n");
}

export async function spawnHandoffChild(params: {
  agentManager: AgentManager;
  agentStorage?: AgentStorage;
  appostleHome: string;
  logger: Logger;
  callerAgentId: string;
  task: string;
  title?: string;
  provider?: string;
  model?: string;
  onHandoffSpawned?: HandoffSpawnedCallback;
}): Promise<ManagedAgent> {
  const parentAgent = params.agentManager.getAgentSnapshot(params.callerAgentId);
  if (!parentAgent) {
    throw new Error(`Parent agent ${params.callerAgentId} not found`);
  }
  if (parentAgent.ownerUserId == null) {
    throw new Error(`handoff failed: parent agent ${params.callerAgentId} has no resolved owner`);
  }

  const targetProvider = params.provider ?? parentAgent.provider;
  const resolved = resolveHandoffModel(
    params.model,
    targetProvider,
    parentAgent.config.model,
    parentAgent.config.thinkingOptionId,
  );
  const targetModeId = getProviderFullAccessModeId(targetProvider) ?? undefined;
  const snapshot = await params.agentManager.spawnAgentFromParent(
    parentAgent,
    {
      provider: targetProvider,
      cwd: parentAgent.cwd,
      modeId: targetModeId,
      title: params.title?.trim() || undefined,
      model: resolved.model,
      thinkingOptionId: resolved.thinkingOptionId,
    },
    {
      labels: {
        "appostle.parent-agent-id": params.callerAgentId,
        "appostle.handoff": "true",
      },
    },
  );

  const taskBrief = params.task.trim();
  const prompt = buildHandoffBriefEnvelope({
    shepherdId: params.callerAgentId,
    shepherdTitle: parentAgent.title ?? parentAgent.config?.title ?? params.callerAgentId,
    taskBrief,
  });

  scheduleAgentMetadataGeneration({
    agentManager: params.agentManager,
    agentId: snapshot.id,
    cwd: snapshot.cwd,
    initialPrompt: taskBrief,
    explicitTitle: snapshot.config.title,
    appostleHome: params.appostleHome,
    logger: params.logger,
    ownerUserId: parentAgent.ownerUserId,
  });

  params.agentManager.recordUserMessage(snapshot.id, prompt, { emitState: false });
  startAgentRun(params.agentManager, snapshot.id, prompt, params.logger);

  if (params.agentStorage) {
    setupFinishNotification({
      agentManager: params.agentManager,
      agentStorage: params.agentStorage,
      childAgentId: snapshot.id,
      callerAgentId: params.callerAgentId,
      logger: params.logger,
    });
  }

  if (params.onHandoffSpawned) {
    void Promise.resolve(
      params.onHandoffSpawned({
        parentAgentId: params.callerAgentId,
        parentAgentCwd: parentAgent.cwd,
        parentAgentOwnerUserId: parentAgent.ownerUserId,
        childAgentId: snapshot.id,
        childAgentTitle: snapshot.config.title ?? null,
        sharees: (snapshot.sessionShares ?? []).map((s) => ({ userId: s.userId })),
      }),
    ).catch((err) => {
      params.logger.warn(
        { err, childAgentId: snapshot.id, parentAgentId: params.callerAgentId },
        "handoff: onHandoffSpawned callback failed",
      );
    });
  }

  return snapshot;
}
